import { useDebugLog } from 'debug/DebugLogContext'
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { User } from 'types'
import { useActiveUser } from '../../api/slack/useActiveUser'
import { useSnackbarContext } from '../SnackbarProvider/SnackbarProvider'

// Generate a unique tab ID for this browser tab (persists across refreshes within same tab)
const getTabId = () => {
  let tabId = sessionStorage.getItem('ws_tab_id')
  if (!tabId) {
    tabId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    sessionStorage.setItem('ws_tab_id', tabId)
  }
  return tabId
}

type WSContextValue = {
  ws: WebSocket | null
  connectedUsers: User[]
  broadcastAnnouncement: (msg: string) => void
  tabId: string
  isDev: boolean
}

const WebsocketContext = createContext<WSContextValue | undefined>(undefined)
export const WebsocketProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { showSnackbar } = useSnackbarContext()
  const [connectedUsers, setConnectedUsers] = useState<User[]>([])
  const [wsReady, setWsReady] = useState(0) // Trigger re-render when WS connects
  const [isDev, setIsDev] = useState(false)
  const { activeUser, isActiveUserLoading } = useActiveUser()
  const { addLog, enabled } = useDebugLog()
  const wsRef = useRef<WebSocket | null>(null)
  const queuedUsersRef = useRef<User[] | null>(null)
  const rafRef = useRef<number | null>(null)
  const tabId = useMemo(() => getTabId(), [])

  const sameUsers = (a: User[] = [], b: User[] = []) => {
    if (a.length !== b.length) return false
    const A = a.map(u => u.id).sort()
    const B = b.map(u => u.id).sort()
    for (let i = 0; i < A.length; i++) if (A[i] !== B[i]) return false
    return true
  }

  const scheduleUsersFlush = () => {
    if (rafRef.current != null) return
    rafRef.current = requestAnimationFrame(() => {
      setConnectedUsers(prev => {
        const next = queuedUsersRef.current ?? []
        return sameUsers(prev, next) ? prev : next
      })
      rafRef.current = null
    })
  }

  const broadcastAnnouncement = (msg: string) => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ message: msg, severity: 'broadcast' }))
    }
  }

  const userInfo = useMemo(() => {
    const user = activeUser?.user ?? {}
    return {
      userId: user.id ?? '',
      userName: user.real_name ?? '',
      firstName: user.profile?.first_name ?? '',
      lastName: user.profile?.last_name ?? '',
      image_512: user?.profile?.image_512 ?? '',
    }
  }, [activeUser])
  useEffect(() => {
    if (isActiveUserLoading || !userInfo.userId) return

    const websocketUrl = import.meta.env.VITE_WEBSOCKET_URL || 'grobotic.app:2096/ws'
    const socket = new WebSocket(
      `wss://${websocketUrl}?userId=${userInfo.userId}&userName=${encodeURIComponent(
        userInfo.userName
      )}&firstName=${encodeURIComponent(userInfo.firstName)}&lastName=${encodeURIComponent(
        userInfo.lastName
      )}&image_512=${encodeURIComponent(userInfo.image_512)}&tabId=${tabId}`
    )

    wsRef.current = socket

    socket.onopen = () => {
      console.log('[WS] Connected')
      if (enabled) addLog({ source: 'ws', level: 'info', label: 'WS connected' })
      // Trigger re-render so consumers get the WebSocket reference
      setWsReady(prev => prev + 1)
    }

    socket.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data)

        switch (data.type) {
          case 'CONNECTED_USERS': {
            // Set dev mode flag from server
            if (data.isDev !== undefined) {
              setIsDev(data.isDev)
            }
            const safeUsers: User[] = (data.users || []).map((u: any) => ({
              id: u.id,
              tabId: u.tabId,
              name: u.name,
              firstName: u.firstName,
              lastName: u.lastName,
              image_512: u.image_512,
              connectedAt: Number(u.connectedAt) || Date.now(),
              isOwnTab: u.isOwnTab || false,
              isOwnOtherTab: u.isOwnOtherTab || false,
            }))
            if (!queuedUsersRef.current || !sameUsers(queuedUsersRef.current, safeUsers)) {
              queuedUsersRef.current = safeUsers
              scheduleUsersFlush()
            }
            if (enabled) {
              addLog({
                source: 'ws',
                level: 'info',
                label: `Connected users: ${safeUsers.length}`,
                meta: {
                  ids: safeUsers.map(u => u.id),
                  names: safeUsers.map(u => u.name),
                },
              })
            }
            break
          }
          case 'DEAL_WON':
            if (data.shouldNotify) showSnackbar(`Deal won! ${data.deal?.name ?? ''}`, 'broadcast')
            break
          case 'BROADCAST_MESSAGE':
            showSnackbar(data.message, data?.severity ?? 'grobotic')
            break

          // domain events: handled elsewhere
          case 'deal.created':
          case 'deal.updated':
          case 'deal.deleted':
          case 'product.created':
          case 'product.updated':
          case 'product.deleted':
          case 'organization.created':
          case 'organization.updated':
          case 'organization.deleted':
            break

          // user activity events: handled by TimeTrackerProvider
          case 'user.page_enter':
          case 'user.page_exit':
          case 'user.activity_update':
            // These events are broadcast to all clients for real-time activity display
            // Can be consumed by components that want to show what other users are viewing
            // Activity events are handled silently - no debug log needed
            // Components can listen for these events via the WebSocket directly
            break

          // Quick Chat events: handled by QuickChatProvider
          case 'direct_message':
          case 'message_sent':
          case 'message_read_receipt':
          case 'typing_indicator':
            // Handled by QuickMessagesProvider via addEventListener
            break

          default:
            if (data.message) showSnackbar(data.message, 'grobotic')
        }
      } catch (e) {
        console.error('[WS] Failed to parse WebSocket message:', e)
      }
    }

    socket.onclose = () => {
      console.log('[WS] Disconnected')
      if (enabled) addLog({ source: 'ws', level: 'warn', label: 'WS disconnected' })
    }

    socket.onerror = err => console.error('[WS] Error:', err)

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      socket.close()
      if (wsRef.current === socket) wsRef.current = null
    }
  }, [isActiveUserLoading, userInfo, showSnackbar, enabled])

  const value = useMemo<WSContextValue>(
    () => ({
      ws: wsRef.current,
      connectedUsers,
      broadcastAnnouncement,
      tabId,
      isDev,
    }),
    [connectedUsers, wsReady, tabId, isDev]
  )

  return <WebsocketContext.Provider value={value}>{children}</WebsocketContext.Provider>
}
export const useWebsocketContext = () => {
  const ctx = useContext(WebsocketContext)
  if (!ctx) throw new Error('useWebsocketContext must be used within WebsocketProvider')
  return ctx
}
